import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { database } from "../../config/firebaseConfig";
import { withRole, withoutRole } from "../../utils/roles";

const initialState = {
  users: {},
  status: "idle",
  error: null,
};

// Fetch all users
export const fetchUsers = createAsyncThunk("users/fetchUsers", async () => {
  const snap = await database.ref("users/users").once("value");
  return snap.val() || {};
});

// Subscribe to users (realtime)
export function subscribeToUsers(dispatch) {
  const ref = database.ref("users/users");
  const callback = ref.on("value", (snap) => {
    dispatch(setUsers(snap.val() || {}));
  });
  return () => ref.off("value", callback);
}

// Save user (create or update in RTDB only - auth user created via Cloud Function)
export const saveUser = createAsyncThunk(
  "users/saveUser",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      if (id) {
        // Update existing user
        await database.ref(`users/users/${id}`).update(data);
        return { id, data };
      } else {
        // Create new user - push to generate ID
        const newRef = database.ref("users/users").push();
        await newRef.set(data);
        return { id: newRef.key, data };
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Create user with Firebase Auth (calls Cloud Function)
export const createAuthUser = createAsyncThunk(
  "users/createAuthUser",
  async ({ email, password, displayName, role }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        "https://us-central1-custom-app-project-test.cloudfunctions.net/createUser",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password, displayName, role }),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Failed to create user");
      }
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// The role list (users/roles). Changes run as transactions, so two admins
// editing it at once don't overwrite each other.
const rolesRef = () => database.ref("users/roles");

// Add `name` (already cleaned) to the role list.
export async function addRole(name) {
  const { committed } = await rolesRef().transaction((list) =>
    withRole(list, name, () => rolesRef().push().key)
  );
  if (!committed) throw new Error(`${name} is already a role.`);
}

// Take `name` off the role list. A list not yet read locally (null) is
// handed back as is, so the transaction reruns on the server's copy.
export async function removeRole(name) {
  await rolesRef().transaction((list) =>
    list === null ? null : withoutRole(list, name)
  );
}

// Roles whose member names are published to the pre-auth readable
// publicRosters node (uid → display name only).
const PUBLIC_ROSTER_ROLES = {
  HYDROTESTER: "hydroTesters",
  "HYDROTEST-TRAINEE": "hydroTesterTrainees",
};

// Update user (RTDB data)
// Pass `uid` (Firebase Auth UID) and `oldRoles` (previous role array) to keep
// the parallel users/user_roles/{role}/{uid} records in sync.
export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, uid, data, oldRoles }, { rejectWithValue }) => {
    try {
      const userUid = uid || data.uid || id;
      const updates = {};

      // Write the main user record fields
      Object.entries(data).forEach(([key, value]) => {
        updates[`users/users/${id}/${key}`] = value;
      });

      const roleChanged = data.role !== undefined;
      const newRoles = Array.isArray(data.role)
        ? data.role
        : data.role
        ? [data.role]
        : [];
      const prevRoles = Array.isArray(oldRoles)
        ? oldRoles
        : oldRoles
        ? [oldRoles]
        : [];

      // Sync parallel user_roles records when role is changing
      if (roleChanged) {
        // Roles being removed → set to false
        prevRoles.forEach((role) => {
          if (!newRoles.includes(role)) {
            updates[`users/user_roles/${role}/${userUid}`] = false;
          }
        });

        // Roles being added/kept → set to true
        newRoles.forEach((role) => {
          updates[`users/user_roles/${role}/${userUid}`] = true;
        });
      }

      // Public rosters: pre-auth readable name lists for select roles
      // (shop kiosk / sign-in screens). Only uid → display name ever
      // leaves the users tree — no emails or profile data.
      const effectiveRoles = roleChanged ? newRoles : prevRoles;
      const rosterName =
        [data.firstName, data.lastName].filter(Boolean).join(" ") ||
        data.displayName ||
        "";
      Object.entries(PUBLIC_ROSTER_ROLES).forEach(([role, node]) => {
        const isMember = effectiveRoles.includes(role);
        if (roleChanged && prevRoles.includes(role) && !isMember) {
          updates[`publicRosters/${node}/${userUid}`] = null;
        } else if (isMember && rosterName) {
          updates[`publicRosters/${node}/${userUid}`] = rosterName;
        }
      });

      await database.ref().update(updates);
      return { id, data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Disable/Enable user
export const toggleUserStatus = createAsyncThunk(
  "users/toggleUserStatus",
  async ({ id, disabled }, { rejectWithValue }) => {
    try {
      await database.ref(`users/users/${id}`).update({ disabled });
      return { id, disabled };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete user (RTDB only - auth deletion via Cloud Function if needed)
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (id, { rejectWithValue }) => {
    try {
      await database.ref(`users/users/${id}`).remove();
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setUsers: (state, action) => {
      state.users = action.payload;
      state.status = "idle";
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = "idle";
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Save user
      .addCase(saveUser.pending, (state) => {
        state.status = "saving";
      })
      .addCase(saveUser.fulfilled, (state, action) => {
        state.status = "idle";
        const { id, data } = action.payload;
        state.users[id] = { ...state.users[id], ...data };
      })
      .addCase(saveUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Create auth user
      .addCase(createAuthUser.pending, (state) => {
        state.status = "creating";
      })
      .addCase(createAuthUser.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(createAuthUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Update user
      .addCase(updateUser.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        state.users[id] = { ...state.users[id], ...data };
      })
      // Toggle status
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        const { id, disabled } = action.payload;
        if (state.users[id]) {
          state.users[id].disabled = disabled;
        }
      })
      // Delete user
      .addCase(deleteUser.fulfilled, (state, action) => {
        delete state.users[action.payload];
      });
  },
});

export const { setUsers, clearError } = usersSlice.actions;

export default usersSlice.reducer;
